import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode
} from "react";

/**
 * Manual-refresh coordination for the dashboard.
 *
 * The dashboard used to refetch every panel's data on every tab switch: each
 * panel unmounts when you navigate away and refetches on remount. That is slow,
 * clunky, and needlessly expensive on the API. Data is now served from a
 * session-scoped cache (see `dashboard-resource.ts`) and only refetched when the
 * user explicitly asks — pull-to-refresh on mobile, the refresh button on
 * desktop. Both funnel through `triggerRefresh()` here, which bumps
 * `refreshToken`; every mounted `useDashboardResource` watches that token and
 * refetches (bypassing the cache) when it changes.
 */
type DashboardRefreshValue = {
  /** Bumped by a manual refresh. Cache-backed resources refetch when it changes. */
  refreshToken: number;
  /** True while at least one manually-triggered refetch is in flight. Drives the
   * spinner on the global refresh control. */
  refreshing: boolean;
  /** Ask every mounted dashboard resource to refetch, bypassing the cache. */
  triggerRefresh: () => void;
  /** Resources bracket their token-driven refetch with these so the global
   * control can show a spinner until the refresh settles. */
  beginRefreshWork: () => void;
  endRefreshWork: () => void;
};

const NOOP_REFRESH: DashboardRefreshValue = {
  refreshToken: 0,
  refreshing: false,
  triggerRefresh: () => {},
  beginRefreshWork: () => {},
  endRefreshWork: () => {}
};

const DashboardRefreshContext = createContext<DashboardRefreshValue | null>(null);

export function DashboardRefreshProvider({ children }: { children: ReactNode }) {
  const [refreshToken, setRefreshToken] = useState(0);
  const [inFlight, setInFlight] = useState(0);

  const triggerRefresh = useCallback(() => {
    setRefreshToken((token) => token + 1);
  }, []);
  const beginRefreshWork = useCallback(() => {
    setInFlight((count) => count + 1);
  }, []);
  const endRefreshWork = useCallback(() => {
    setInFlight((count) => Math.max(0, count - 1));
  }, []);

  const value = useMemo<DashboardRefreshValue>(
    () => ({
      refreshToken,
      refreshing: inFlight > 0,
      triggerRefresh,
      beginRefreshWork,
      endRefreshWork
    }),
    [refreshToken, inFlight, triggerRefresh, beginRefreshWork, endRefreshWork]
  );

  return (
    <DashboardRefreshContext.Provider value={value}>
      {children}
    </DashboardRefreshContext.Provider>
  );
}

/**
 * Panels rendered outside a `DashboardRefreshProvider` (isolated tests, stories)
 * degrade gracefully to "fetch once on mount, no global refresh" rather than
 * crashing on a missing provider.
 */
export function useDashboardRefresh(): DashboardRefreshValue {
  return useContext(DashboardRefreshContext) ?? NOOP_REFRESH;
}
